// Frontend authentication utilities

import { User, Tenant, UserRole } from '../../shared/types'
import { apiClient } from './api'

export interface AuthUser {
  id: string
  email: string
  name?: string
  role: UserRole
  tenant: {
    id: string
    name: string
    slug: string
    creditsRemaining: number
  }
}

export interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

export class AuthService {
  private listeners: Array<(state: AuthState) => void> = []
  private currentState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  }

  constructor() {
    this.initializeAuth()
  }

  private async initializeAuth(): Promise<void> {
    const token = localStorage.getItem('authToken')
    
    if (!token) {
      this.updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      })
      return
    }

    try {
      const user = await apiClient.getCurrentUser()
      this.updateState({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      })
    } catch (error) {
      console.error('Auth initialization failed:', error)
      localStorage.removeItem('authToken')
      this.updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authentication failed'
      })
    }
  }

  private updateState(newState: AuthState): void {
    this.currentState = newState
    this.listeners.forEach(listener => listener(newState))
  }

  public subscribe(listener: (state: AuthState) => void): () => void {
    this.listeners.push(listener)
    // Call immediately with current state
    listener(this.currentState)
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  public getState(): AuthState {
    return this.currentState
  }

  public async login(email: string, password: string): Promise<void> {
    this.updateState({
      ...this.currentState,
      isLoading: true,
      error: null
    })

    try {
      const response = await apiClient.login({ email, password })
      
      this.updateState({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      })
    } catch (error) {
      this.updateState({
        ...this.currentState,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed'
      })
      throw error
    }
  }

  public async logout(): Promise<void> {
    this.updateState({
      ...this.currentState,
      isLoading: true
    })

    try {
      await apiClient.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      this.updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      })
    }
  }

  public hasPermission(permission: string): boolean {
    if (!this.currentState.user) return false

    // Import permission mapping from shared constants
    const { USER_ROLE_PERMISSIONS } = require('../../shared/utils/constants')
    const userPermissions = USER_ROLE_PERMISSIONS[this.currentState.user.role] || []
    
    return userPermissions.includes(permission)
  }

  public isAdmin(): boolean {
    return this.currentState.user?.role === UserRole.ADMIN
  }

  public isEditor(): boolean {
    return this.currentState.user?.role === UserRole.EDITOR || this.isAdmin()
  }

  public getTenantId(): string | null {
    return this.currentState.user?.tenant.id || null
  }

  public getCreditsRemaining(): number {
    return this.currentState.user?.tenant.creditsRemaining || 0
  }
}

// Create singleton instance
export const authService = new AuthService()

// Demo account credentials for easy access
export const DEMO_CREDENTIALS = [
  {
    email: 'demo@techstartup.com',
    password: 'demo123',
    label: 'TechStartup Inc'
  },
  {
    email: 'demo@retailstore.com',
    password: 'demo123',
    label: 'Retail Store Co'
  },
  {
    email: 'demo@consultancy.com',
    password: 'demo123',
    label: 'Business Consultancy'
  }
] as const

// Local storage keys
export const AUTH_STORAGE_KEYS = {
  TOKEN: 'authToken',
  USER: 'authUser',
  REMEMBER_ME: 'rememberMe'
} as const

// Auth guard utilities
export function requireAuth(): boolean {
  const { isAuthenticated } = authService.getState()
  
  if (!isAuthenticated) {
    // Redirect to login in a React app context
    window.location.href = '/login'
    return false
  }
  
  return true
}

export function requirePermission(permission: string): boolean {
  if (!requireAuth()) return false
  
  if (!authService.hasPermission(permission)) {
    // Handle insufficient permissions
    console.warn(`Access denied: missing permission ${permission}`)
    return false
  }
  
  return true
}

// Route protection helpers
export const PROTECTED_ROUTES = [
  '/dashboard',
  '/pitches',
  '/content',
  '/library',
  '/calendar',
  '/analytics',
  '/settings'
] as const

export const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/signup',
  '/about',
  '/pricing'
] as const

export function isProtectedRoute(path: string): boolean {
  return PROTECTED_ROUTES.some(route => path.startsWith(route))
}

export function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some(route => path === route || path.startsWith(route))
}